import AICampusFooter from "@/components/probaworkflows_base_components/AICampusFooter/AiFooter.vue";
import GlobalTable from "@/components/probaworkflows_base_components/GlobalTable/GlobalTable.vue";
import {
  GET_ALL_USER_APPLICATIONS,
  GET_ENTITY_BY_USER,
  get_headers,
  LIST_ENTITY_ASSESSMENTS,
  VIEW_FORM_STATUS,
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import formatting_mixins from "@/utils/mixins/formatting_mixins";
import axios from "axios";

function build_status_filter_option(item) {
  const status_filter_option = {
    value: String(item?.id ?? ""),
    label: item?.status ?? "",
    raw_status: item?.status ?? "",
  };

  return status_filter_option;
}

export default {
  name: "ViewTrustForm",
  mixins: [alert_mixin, formatting_mixins],
  components: {
    GlobalTable,
    AICampusFooter,
  },
  data: () => ({
    hostname: window.location.hostname,
    activeFileTab: "employee", // Default active file tab
    activeSectionTab: "employment-info", // Default active section tab
    headers: "",
    application_list: [],
    application_table_headers: [],
    table_safe_application_list: [],
    selected_status: null,
    filter_by: [],
    application_row_map: new Map(),
    stored_form_count: null,
    total_assessment_count: 0,
    entities_for_user: [],
    total_pre_assessment_count: 0,
    total_post_assessment_count: 0,
    form_count: 0,
    status_filter_options: [],
    table_render_key: 0,
    is_loading_forms: false,
  }),

  async mounted() {
    this.headers = get_headers();
    await this.fetch_status_filter_options();
    this.apply_status_from_route();
    this.get_user_forms(this.selected_status);
    // this.fetch_assessments();
    this.fetch_entities_by_user();
  },
  watch: {
   "$route.query.status_id"() {
      window.location.reload();
    },
  },

  methods: {
    async fetch_status_filter_options() {
      await axios.get(VIEW_FORM_STATUS, {
        headers: this.headers,
        params: {
          project_id: sessionStorage.getItem("project_id"),
        },
      }).then((response) => {
        const status_data = response.data?.data || [];
        this.status_filter_options = status_data.map(build_status_filter_option);
      }).catch(() => {
        this.status_filter_options = [];
      });
    },

    set_status_filter(value) {
      this.selected_status = value || "";
      sessionStorage.setItem("filter_by", JSON.stringify([this.selected_status]));
      this.get_user_forms(this.selected_status || null);
    },

    apply_status_from_route() {
      const route_status = this.$route.query.status_id;

      if (!route_status) {
        this.selected_status = "";
        return;
      }

      const matched_status = (this.status_filter_options || []).find((option) =>
        String(option?.value) === String(route_status) ||
        option?.raw_status === route_status ||
        option?.label === route_status
      );

      this.selected_status = matched_status?.value || route_status || "";
    },

    get_user_forms(filter = null) {
      this.application_list = [];
      this.is_loading_forms = true;

      return this.show_loader("Loading applications...")
        .then((loader) => {
          const params = {
            include_drafts: true,
          };
          if (filter) {
            params.filter_by = filter;
          }

          return axios
            .get(GET_ALL_USER_APPLICATIONS, {
              headers: this.headers,
              params,
            })
            .then((response) => {
              const data = response.data?.data || [];
              this.application_list = data;
              this.prep_tables();
              this.table_render_key += 1;
            })
            .catch((error) => {
              const message =
                error.response?.data?.message || error.message || "Unknown error";
              this.show_error("Error getting applications: " + message);
            })
            .finally(() => {
              this.is_loading_forms = false;
              return this.close_loader(loader);
            });
        });
    },

    prep_tables() {
      this.application_row_map = new Map();

      const rows = (this.application_list || []).map((application) => {
        const child = application?.current_entity;
        const child_type = application?.current_entity_type;
        const entity_name = this.get_entity_name(application);
        const application_number = application?.id ?? "";
        const row = {
          "Application No": application?.id ?? "",
          "Name": entity_name, // Use the computed entity name
          "Category": child_type?.entity_type_name ?? application?.entity_type?.entity_type_name ?? "",
          "Date Created": this.formatApplicationDate(application?.date_created),
          Status: (application?.status?.status ?? "").replace("_", " "),
        };

        this.application_row_map.set(String(application_number), {
          application_id: application_number,
          status: row.Status,
          entity_profile_id: child?.id ?? application?.entity ?? null,
          entity_type_id: application?.entity_type?.id ?? null,
          entity_name,
          entity_type_name: child_type?.entity_type_name ?? application?.entity_type?.entity_type_name ?? "",
        });

        return row;
      });

      this.application_table_headers = rows.length
        ? Object.keys(rows[0])
        : ["Application No", "Name", "Category", "Date Created", "Status"];

      this.table_safe_application_list = rows;
    },

    get_entity_name(application) {

      if (application?.current_entity_name) {
        return application.current_entity_name.trim();
      }

      const child_user = application?.current_entity?.user;
      if (child_user?.first_name && child_user?.last_name) {
        return `${child_user.first_name} ${child_user.last_name}`.trim();
      }

      const parent_user = application?.profile_role?.[0]?.user;
      if (parent_user?.first_name && parent_user?.last_name) {
        return `${parent_user.first_name} ${parent_user.last_name}`.trim();
      }
      return "Unknown Entity";
    },


    // Add this helper method to handle the date formatting
    formatApplicationDate(dateString) {
      if (!dateString) return "";

      try {
        // If the date is already in the format "Tue 19 August 2025 09:48"
        if (typeof dateString === "string" && dateString.includes("August")) {
          return dateString;
        }

        // If it's a proper date string that can be parsed
        const date = new Date(dateString);
        return new Intl.DateTimeFormat("en-GB", {
          weekday: "short",
          day: "numeric",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(date);
      } catch (error) {
        return dateString;
      }
    },

    view_entity_application_overview(row) {
        const application = this.application_row_map.get(String(row?.["Application No"]));
        if (!application) {
          this.show_error("Unable to open application overview.");
          return;
        }

        const {
          application_id,
          status,
          entity_profile_id,
          entity_type_id,
          entity_name,
          entity_type_name,
        } = application;

        // Persist data
        sessionStorage.setItem("application_id", application_id);
        sessionStorage.setItem("application_status", status);
        sessionStorage.setItem("entity_profile_id", entity_profile_id);
        sessionStorage.setItem("entity_type_id", entity_type_id);
        sessionStorage.setItem("entity_name", entity_name);
        sessionStorage.setItem("entity_type_name", entity_type_name);

        const type = (entity_type_name);
        let route_name = "";

        if (type === "Trustee") {
          route_name = "trustee_view_dashboard";
        }
        else if (type === "Beneficiary") {
          route_name = "beneficiary_dashboard";
        } else if (type === "Founder") {
          route_name = "founder_dashboard";
        }else {
          route_name = "founder_dashboard"; //default view for other entity types
        }


        // Navigate
        this.$router.push({
          name: route_name,
          params: {
            application_no: application_id,
            status: status,
            entity_profile_id: entity_profile_id,
            entity_type_id: entity_type_id,
            entity_name: entity_name,
            entity_type_name: entity_type_name
          },
          query: {
            application_id: application_id,
            entity_profile_id: entity_profile_id,
            entity_type_id: entity_type_id,
            status: status,
            entity_name: entity_name,
            entity_type_name: entity_type_name
          }
        });
      },

    async fetch_assessments() {
      const entity_type_id = sessionStorage.getItem("entity_type_id");
      if (!entity_type_id) {
        return;
      }

      try {
        const resp = await axios.get(LIST_ENTITY_ASSESSMENTS, {
          params: { entity_type_id: entity_type_id },
          headers: this.headers,
        });
        const body = resp.data;
        if (body.status === "success") {
          this.total_assessment_count = body.total_assessments;
          this.total_pre_assessment_count = body.total_pre_assessments;
          this.total_post_assessment_count = body.total_post_assessments;
        } else {
          this.total_assessment_count = 0;
        }
      } catch (err) {
        if (err.response?.status === 404) {
          this.total_assessment_count = 0;
          return;
        }
        let payload = err.response?.data;
        if (typeof payload === "string") {
          try {
            payload = JSON.parse(payload);
          } catch {
            payload = { message: "Error parsing response" };
          }
        }
        const msg = payload?.message || err.message;
        this.show_error("Assessments", msg);
      }
    },

    async fetch_entities_by_user() {
      const user_id = sessionStorage.getItem("user_id");
      if (!user_id) return;
      await this.load_user_entities(user_id);
    },

    /* eslint-disable no-useless-catch */
    load_user_entities(user_id) {
      return axios
        .get(GET_ENTITY_BY_USER, {
          params: { user_id: user_id, filter_by: this.selected_status },
          headers: this.headers || {},
        })
        .then(({ data: body }) => {
          if (body.status === "success") {
            this.entities_for_user = body.data;
            if (this.entities_for_user.length) {
              sessionStorage.setItem(
                "entity_type_id",
                this.entities_for_user[0].entity_type
              );
              sessionStorage.setItem("entity_id", this.entities_for_user[0].id);
            }
          } else {
            this.show_error(
              "Entities",
              body.message || "Could not load entities"
            );
          }
        })
        .catch((err) => {
          if (err.response?.status === 404) return;

          const payload =
            typeof err.response?.data === "string"
              ? JSON.parse(err.response.data)
              : err.response?.data || {};
          this.show_error("Entities", payload.message || err.message);
        });
    },

    redirect_to_view_all_user_assessments() {
      this.$router.push({
        path: "/entity_risk_assessment",
      });
    },
  },
};
